import React, { useState, useCallback, useEffect, useRef } from 'react';
import apiService from '../../services/apiService';
import GaleriaPickerModal from '../galeria/GaleriaPickerModal';
import FotoPreviewModal from '../common/FotoPreviewModal';
import useAuthenticatedImageLoader from '../../hooks/useAuthenticatedImageLoader';
import { compressImage } from '../../utils/imageUtils';
import { useToast } from '../contexts/ToastContext';

/**
 * Thumbnail de foto de hallazgo con lazy loading (patrón EvidenciaThumb).
 * Usa endpoints propios del módulo hallazgos en vez de galería.
 *
 * @param {Object} props
 * @param {number} props.hallazgoId - ID del hallazgo
 * @param {number} props.fotoId - ID de la foto_galeria
 * @param {string} [props.descripcion] - Descripción de la foto
 * @returns {JSX.Element}
 */
function HallazgoThumb({ hallazgoId, fotoId, descripcion }) {
  const { ref, blobUrl, loading, failed } = useAuthenticatedImageLoader(
    () => apiService.downloadThumbnailHallazgo(hallazgoId, fotoId),
    `hallazgo-${hallazgoId}-${fotoId}`
  );
  return (
    <div ref={ref} className="galeria-thumb-wrapper">
      {loading && !blobUrl && (
        <div className="galeria-thumb-loading"><i className="fas fa-spinner fa-spin" /></div>
      )}
      {failed && (
        <div className="galeria-thumb-error"><i className="fas fa-image" /></div>
      )}
      {blobUrl && <img src={blobUrl} alt={descripcion || ''} className="galeria-thumb" />}
    </div>
  );
}

/**
 * Fullscreen modal para ver imagen completa.
 * Usa endpoint propio del módulo hallazgos.
 *
 * @param {Object} props
 * @param {number} props.hallazgoId - ID del hallazgo
 * @param {number|null} props.fotoId - ID de la foto a mostrar
 * @param {Function} props.onClose - Callback al cerrar
 * @returns {JSX.Element|null}
 */
function FullscreenModal({ hallazgoId, fotoId, onClose }) {
  const { ref, blobUrl, loading, failed } = useAuthenticatedImageLoader(
    () => apiService.downloadFotoHallazgo(hallazgoId, fotoId),
    fotoId ? `hallazgo-full-${hallazgoId}-${fotoId}` : null
  );

  if (!fotoId) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
        <div className="modal-header">
          <h3 className="modal-title"><i className="fas fa-image" /> Foto</h3>
          <button className="modal-close-btn" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="modal-body" ref={ref} style={{ textAlign: 'center', minHeight: '200px' }}>
          {loading && <div className="galeria-empty"><i className="fas fa-spinner fa-spin fa-2x" /></div>}
          {failed && <div className="galeria-empty"><i className="fas fa-exclamation-triangle fa-2x" /><p>Error al cargar imagen</p></div>}
          {blobUrl && <img src={blobUrl} alt="Foto hallazgo" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />}
        </div>
      </div>
    </div>
  );
}

/**
 * Panel lateral de fotos de un hallazgo
 *
 * @param {Object} props
 * @param {number} props.hallazgoId - ID del hallazgo
 * @param {string} props.hallazgoCodigo - Código del hallazgo para el título
 * @param {Function} props.onClose - Callback al cerrar
 * @param {boolean} props.canEdit - Si puede agregar fotos
 * @param {boolean} props.canDelete - Si puede desvincular fotos
 * @returns {JSX.Element}
 */
function HallazgoFotosPanel({ hallazgoId, hallazgoCodigo, onClose, canEdit, canDelete }) {
  const { showToast } = useToast();
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [fullscreenFotoId, setFullscreenFotoId] = useState(null);

  const fetchFotos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getHallazgoFotos(hallazgoId);
      if (response.success) setFotos(response.data || []);
    } catch (err) {
      showToast('Error al cargar fotos', 'error');
    } finally {
      setLoading(false);
    }
  }, [hallazgoId, showToast]);

  useEffect(() => {
    if (hallazgoId) fetchFotos();
  }, [hallazgoId, fetchFotos]);

  const resetUploadState = useCallback(() => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [preview]);

  const handleFileSelect = useCallback(async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const compressed = await compressImage(selectedFile);
    setFile(compressed);
    setPreview(URL.createObjectURL(compressed));
  }, []);

  const handleUpload = useCallback(async (descripcion) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      if (descripcion) formData.append('descripcion', descripcion);
      const response = await apiService.uploadFotoHallazgo(hallazgoId, formData);
      if (response.success) {
        showToast('Foto subida', 'success');
        resetUploadState();
        fetchFotos();
      }
    } catch (err) {
      showToast('Error al subir foto', 'error');
    } finally {
      setUploading(false);
    }
  }, [file, hallazgoId, fetchFotos, showToast, resetUploadState]);

  const handlePickerSelect = useCallback(async (foto) => {
    try {
      const response = await apiService.vincularFotoAHallazgo(hallazgoId, { foto_galeria_id: foto.id });
      if (response.success) {
        showToast('Foto vinculada', 'success');
        fetchFotos();
      }
    } catch (err) {
      showToast('Error al vincular foto', 'error');
    }
  }, [hallazgoId, fetchFotos, showToast]);

  const handleDesvincular = useCallback(async (fotoGaleriaId) => {
    try {
      const response = await apiService.desvincularFotoDeHallazgo(hallazgoId, fotoGaleriaId);
      if (response.success) {
        showToast('Foto desvinculada', 'success');
        fetchFotos();
      }
    } catch (err) {
      showToast('Error al desvincular foto', 'error');
    }
  }, [hallazgoId, fetchFotos, showToast]);

  return (
    <>
      <div className="sidebar-overlay" onClick={onClose} />
      <div className="sidebar-responsive-lg active bg-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            <h3 className="sidebar-title">
              <i className="fas fa-camera"></i> Fotos: {hallazgoCodigo}
            </h3>
            <p className="sidebar-subtitle">{fotos.length} foto(s)</p>
          </div>
          <button
            className="sidebar-close"
            onClick={onClose}
            aria-label="Cerrar panel de fotos"
            title="Cerrar"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="documentos-content-container">
          <div className="documentos-scroll-area" style={{ padding: '0 1rem' }}>
            {loading && (
              <div className="documentos-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Cargando fotos…</span>
              </div>
            )}
            {uploading && (
              <div className="documentos-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <span>Subiendo foto…</span>
              </div>
            )}
            {!loading && fotos.length === 0 && (
              <div className="documentos-empty">
                <i className="fas fa-camera"></i>
                <h4>Sin fotos</h4>
                <p>Sube o adjunta fotos desde la galería</p>
              </div>
            )}
            {!loading && fotos.length > 0 && (
              <div className="galeria-grid">
                {fotos.map(foto => (
                  <div key={foto.id} className="galeria-item">
                    <div onClick={() => setFullscreenFotoId(foto.foto_galeria_id)} style={{ cursor: 'pointer' }}>
                      <HallazgoThumb hallazgoId={hallazgoId} fotoId={foto.foto_galeria_id} descripcion={foto.descripcion} />
                    </div>
                    <div className="galeria-item-overlay">
                      <div className="overlay-actions">
                        <button
                          className="overlay-action-btn"
                          title="Ver"
                          onClick={() => setFullscreenFotoId(foto.foto_galeria_id)}
                        >
                          <i className="fas fa-expand" />
                        </button>
                        {canDelete && (
                          <button
                            className="overlay-action-btn overlay-action-btn--danger"
                            title="Desvincular"
                            onClick={() => handleDesvincular(foto.foto_galeria_id)}
                          >
                            <i className="fas fa-unlink" />
                          </button>
                        )}
                      </div>
                      {foto.descripcion && <span className="overlay-desc">{foto.descripcion}</span>}
                    </div>
                    {foto.descripcion && (
                      <div className="galeria-item-meta">
                        <p className="galeria-item-desc" title={foto.descripcion}>{foto.descripcion}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {canEdit && (
            <div className="documentos-footer">
              <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                <i className="fas fa-camera"></i> Subir foto
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  disabled={uploading}
                />
              </label>
              <button className="btn btn-secondary" onClick={() => setPickerOpen(true)}>
                <i className="fas fa-images"></i> Desde galería
              </button>
            </div>
          )}
        </div>
      </div>

      <FotoPreviewModal
        isOpen={!!(file && preview)}
        previewSrc={preview}
        title="Agregar foto"
        uploading={uploading}
        onSave={handleUpload}
        onCancel={resetUploadState}
      />

      <GaleriaPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
      />

      <FullscreenModal
        hallazgoId={hallazgoId}
        fotoId={fullscreenFotoId}
        onClose={() => setFullscreenFotoId(null)}
      />
    </>
  );
}

export default HallazgoFotosPanel;
